import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  ResponsiveContainer, AreaChart, Area, LineChart, Line,
  PieChart, Pie, Cell, Legend,
  XAxis, YAxis, Tooltip, CartesianGrid, ReferenceLine,
} from 'recharts';
import {
  RefreshCw, Droplet, AlertCircle, BarChart3,
} from 'lucide-react';
import { API } from '../../App';
import { useAlert } from '../../context/AlertContext';
import useLegendToggle from '../../utils/useLegendToggle';

/**
 * M4 Water Dashboard — intake (fresh), recycled, recycle %, water-SEU
 * (kL per unit production), per-water-type stacked daily trend, recycle ratio
 * trend with 50% target. Reads /api/oim/utilities/water.
 */

const PERIOD_OPTIONS = [
  { id: '7d',   label: 'Last 7 days' },
  { id: '30d',  label: 'Last 30 days' },
  { id: '90d',  label: 'Last 90 days' },
  { id: '180d', label: 'Last 180 days' },
];

// Color palette per water_type (kept consistent across stacked area + donut).
const WATER_TYPE_COLOR = {
  process:    '#3b82f6',
  cooling:    '#06b6d4',
  domestic:   '#8b5cf6',
  recycled:   '#10b981',
  drinking:   '#0ea5e9',
  raw:        '#64748b',
  borewell:   '#a855f7',
  municipal:  '#0284c7',
};
const COLOR_FALLBACK = ['#3b82f6', '#06b6d4', '#8b5cf6', '#10b981', '#f59e0b', '#dc2626', '#0ea5e9'];

const fmt = (n, d = 2) => n == null || isNaN(n) ? '—'
  : Number(n).toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
const fmtCompact = (n) => {
  if (n == null || isNaN(n)) return '—';
  const v = Number(n);
  if (Math.abs(v) >= 1e6) return (v / 1e6).toFixed(2) + 'M';
  if (Math.abs(v) >= 1e3) return (v / 1e3).toFixed(2) + 'K';
  return v.toFixed(0);
};

export default function OimWaterPanel({ siteId }) {
  const { showError } = useAlert();
  const [period, setPeriod] = useState('30d');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  // Click-to-toggle legend state for the stacked-area chart and the donut.
  const trendLegend = useLegendToggle();
  const donutLegend = useLegendToggle();

  const fetchData = useCallback(async () => {
    if (!siteId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams({ site_id: siteId, period });
      const res = await axios.get(`${API}/oim/utilities/water?${params}`);
      setData(res.data);
    } catch (e) {
      console.error(e);
      showError(e.response?.data?.detail || 'Failed to load water dashboard');
    } finally { setLoading(false); }
  }, [siteId, period, showError]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const colorForType = (t, idx = 0) => WATER_TYPE_COLOR[String(t).toLowerCase()] || COLOR_FALLBACK[idx % COLOR_FALLBACK.length];

  const byTypeWithColor = useMemo(() => {
    return (data?.by_water_type || []).map((d, i) => ({ ...d, fill: colorForType(d.water_type, i) }));
  }, [data]);

  if (!siteId) return (
    <div className="p-12 text-center text-gray-500">
      <AlertCircle className="w-10 h-10 mx-auto text-gray-300 mb-2"/>
      Select a site to view water dashboard.
    </div>
  );

  const k = data?.kpis || {};
  const types = data?.water_types_seen || [];

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Droplet className="w-5 h-5 text-blue-600"/> Water Dashboard
          </h2>
          <div className="text-xs text-gray-500">Intake, recycled, recycle %, SEU per unit production</div>
        </div>
        <div className="flex items-center gap-2">
          <select value={period} onChange={e => setPeriod(e.target.value)} className="px-2 py-1.5 border rounded text-sm">
            {PERIOD_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
          </select>
          <button onClick={fetchData} disabled={loading} className="p-2 text-gray-500 hover:text-gray-700">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}/>
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
        <Kpi label="Fresh Water Intake" value={`${fmt(k.water_intake_kl, 1)} kL`} sub="Net draw from external sources" accent="blue" loading={loading}/>
        <Kpi label="Total Throughput" value={`${fmt(k.water_throughput_kl ?? k.water_total_kl, 1)} kL`} sub={`Fresh + ${fmtCompact(k.water_reused_kl)} kL reused (recycled / condensate / ZLD)`} accent="cyan" loading={loading}/>
        <Kpi label="Recycle %" value={k.recycle_pct != null ? `${fmt(k.recycle_pct, 1)}%` : '—'} sub={`${fmtCompact(k.water_recycled_kl)} kL recycled`} accent="emerald" loading={loading}/>
        <Kpi label="Fresh Water SEU" value={k.water_seu != null ? `${fmt(k.water_seu, 3)} kL/${k.production_unit || 'unit'}` : '—'} sub="Intake ÷ production · lower = better" accent="violet" loading={loading}/>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-1 gap-3 mb-6">
        <Kpi
          label="ZLD Recovery %"
          value={k.zld_recovery_pct != null ? `${fmt(k.zld_recovery_pct, 1)}%` : '—'}
          sub={k.has_zld_meter
            ? `${fmtCompact(k.zld_recovered_kl)} kL recovered ÷ ${fmtCompact(k.zld_input_kl)} kL fed to ZLD`
            : "Upload water_type='zld_recovered' (MEE+RO output) and waste_type='zld_input' (vinasse fed to ZLD) rows to populate"}
          accent={k.zld_recovery_pct != null ? 'emerald' : 'gray'}
          loading={loading}
        />
      </div>

      {/* Daily stacked area trend */}
      <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
        <h3 className="text-sm font-semibold text-gray-700 mb-2">Daily Water Consumption by Type</h3>
        <p className="text-xs text-gray-500 mb-2">Stacked kL per day across {types.length || 0} water types.</p>
        {(data?.daily_trend || []).length > 0 && types.length > 0 ? (
          <ResponsiveContainer width="100%" height={240}>
            <AreaChart data={data.daily_trend}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
              <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}/>
              <YAxis tick={{ fontSize: 10 }}/>
              <Tooltip formatter={(v, n) => [`${fmt(v, 2)} kL`, n]}/>
              <Legend
                wrapperStyle={{ fontSize: 11 }}
                onClick={trendLegend.onLegendClick}
                formatter={trendLegend.legendFormatter}
              />
              {types.map((t, i) => (
                <Area
                  key={t}
                  type="monotone"
                  dataKey={t}
                  stackId="1"
                  stroke={colorForType(t, i)}
                  fill={colorForType(t, i)}
                  fillOpacity={0.6}
                  strokeWidth={1.5}
                  hide={trendLegend.isHidden(t)}
                />
              ))}
            </AreaChart>
          </ResponsiveContainer>
        ) : <Empty hint="No water data — upload water CSV with water_type column"/>}
      </div>

      {/* By water type donut + recycle trend */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Mix by Water Type</h3>
          <p className="text-xs text-gray-500 mb-2">Total kL share for the period.</p>
          {byTypeWithColor.length > 0 ? (() => {
            // Keep ALL slices in the data array so the legend always lists every
            // water type. Hidden slices just have their value zeroed — the Pie
            // collapses their wedge to 0° but the entry stays in the legend
            // payload so users can click it back on.
            const totalVisible = byTypeWithColor.reduce(
              (s, x) => s + (donutLegend.isHidden(x.water_type) ? 0 : x.kl), 0,
            ) || 1;
            const renderData = byTypeWithColor.map(d => ({
              ...d,
              kl: donutLegend.isHidden(d.water_type) ? 0 : d.kl,
            }));
            return (
              <ResponsiveContainer width="100%" height={240}>
                <PieChart>
                  <Pie
                    data={renderData}
                    dataKey="kl"
                    nameKey="water_type"
                    cx="50%"
                    cy="50%"
                    innerRadius={48}
                    outerRadius={78}
                    paddingAngle={2}
                  >
                    {renderData.map((entry, i) => (
                      <Cell key={i} fill={entry.fill}/>
                    ))}
                  </Pie>
                  <Legend
                    layout="vertical"
                    align="right"
                    verticalAlign="middle"
                    wrapperStyle={{ fontSize: 11 }}
                    onClick={(entry) => donutLegend.onLegendClick({ value: entry.value })}
                    formatter={(value) => {
                      const item = byTypeWithColor.find(d => d.water_type === value);
                      const dim = donutLegend.isHidden(value);
                      const label = item
                        ? `${item.water_type} · ${fmt(item.kl, 1)} kL (${((item.kl / totalVisible) * 100).toFixed(0)}%)`
                        : value;
                      return (
                        <span
                          style={{
                            opacity: dim ? 0.4 : 1,
                            textDecoration: dim ? 'line-through' : 'none',
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                          title={dim ? 'Click to show' : 'Click to hide'}
                        >{label}</span>
                      );
                    }}
                  />
                  <Tooltip formatter={v => `${fmt(v, 2)} kL`}/>
                </PieChart>
              </ResponsiveContainer>
            );
          })() : <Empty hint="No water-type rollup yet"/>}
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-2">Recycle Ratio Trend</h3>
          <p className="text-xs text-gray-500 mb-2">Daily recycled ÷ total water · target line at 50%.</p>
          {(data?.recycle_trend || []).length > 0 ? (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.recycle_trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" vertical={false}/>
                <XAxis dataKey="day" tick={{ fontSize: 10 }} tickFormatter={v => v ? new Date(v).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : ''}/>
                <YAxis tick={{ fontSize: 10 }} domain={[0, 100]} tickFormatter={v => `${v}%`}/>
                <Tooltip formatter={v => v != null ? `${fmt(v, 1)}%` : '—'}/>
                <ReferenceLine y={50} stroke="#10b981" strokeDasharray="3 3" label={{ value: 'Target 50%', fontSize: 10, fill: '#10b981' }}/>
                <Line type="monotone" dataKey="pct" stroke="#059669" strokeWidth={2} dot={{ r: 2 }}/>
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty hint="Need recycled-water rows to plot ratio trend"/>}
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, accent = 'gray', loading }) {
  const accents = {
    blue:    { bg: 'bg-blue-50',    text: 'text-blue-700' },
    cyan:    { bg: 'bg-cyan-50',    text: 'text-cyan-700' },
    emerald: { bg: 'bg-emerald-50', text: 'text-emerald-700' },
    violet:  { bg: 'bg-violet-50',  text: 'text-violet-700' },
    gray:    { bg: 'bg-gray-50',    text: 'text-gray-700' },
  };
  const a = accents[accent] || accents.gray;
  if (loading) return (
    <div className={`p-3 rounded-lg border border-gray-200 ${a.bg} animate-pulse`}>
      <div className="h-3 w-20 bg-gray-200 rounded mb-2"/>
      <div className="h-6 w-28 bg-gray-200 rounded"/>
    </div>
  );
  return (
    <div className={`p-3 rounded-lg border border-gray-200 ${a.bg}`}>
      <div className={`text-[10px] font-semibold uppercase tracking-wide ${a.text}`}>{label}</div>
      <div className="text-xl font-bold text-gray-900 mt-1 truncate">{value}</div>
      {sub && <div className="text-[11px] text-gray-500 mt-0.5">{sub}</div>}
    </div>
  );
}

function Empty({ hint }) {
  return (
    <div className="flex flex-col items-center justify-center text-center text-gray-400 py-8 text-xs">
      <BarChart3 className="w-8 h-8 text-gray-300 mb-2"/>
      <div>{hint}</div>
    </div>
  );
}
